import { useState } from "react";
import { ChevronDown, Check, ShieldAlert, ShieldCheck, Shield, ShieldQuestion, Sparkles, UserRound } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { RISQUES, updateRisque } from "@/lib/dossiers";
import { cn } from "@/lib/utils";

export const RISQUE_STYLE = {
    "Élevé": { cls: "bg-rose-100 text-rose-800 hover:bg-rose-200", icon: ShieldAlert },
    Vigilance: { cls: "bg-amber-100 text-amber-800 hover:bg-amber-200", icon: Shield },
    Faible: { cls: "bg-emerald-100 text-emerald-800 hover:bg-emerald-200", icon: ShieldCheck },
    "Non évalué": { cls: "bg-muted text-muted-foreground hover:bg-accent", icon: ShieldQuestion },
};

export default function RisqueBadge({ dossierId, value, source, onChange, size = "sm" }) {
    const [busy, setBusy] = useState(false);
    const niveau = RISQUES.includes(value) ? value : "Non évalué";
    const { cls, icon: Icon } = RISQUE_STYLE[niveau];

    async function select(n) {
        if (n === niveau && source === "manuel") return;
        setBusy(true);
        try {
            await updateRisque(dossierId, n, "manuel");
            onChange?.(n, "manuel");
            toast.success("Niveau de risque forcé", { description: `${n} — défini manuellement par l'expert.` });
        } catch (e) {
            toast.error("Mise à jour impossible", { description: e.message });
        } finally {
            setBusy(false);
        }
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    data-testid={`risque-badge-${dossierId}`}
                    data-risque={niveau}
                    disabled={busy}
                    onClick={(e) => e.stopPropagation()}
                    title={source === "manuel" ? "Forcé manuellement par l'expert" : "Déduit de l'analyse IA / Pappers"}
                    className={cn(
                        "inline-flex items-center gap-1.5 rounded-full font-medium transition-colors disabled:opacity-60",
                        size === "lg" ? "px-4 h-10 text-sm" : "px-2.5 h-7 text-xs",
                        cls
                    )}
                >
                    <Icon className={size === "lg" ? "h-4 w-4" : "h-3.5 w-3.5"} strokeWidth={2} />
                    {niveau}
                    {source === "manuel" ? <UserRound className="h-3 w-3 opacity-70" /> : niveau !== "Non évalué" && <Sparkles className="h-3 w-3 opacity-70" />}
                    <ChevronDown className="h-3 w-3 opacity-60" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()} className="min-w-[200px]">
                <p className="px-2 py-1.5 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Forcer le niveau</p>
                {RISQUES.map((n) => {
                    const I = RISQUE_STYLE[n].icon;
                    return (
                        <DropdownMenuItem key={n} data-testid={`risque-option-${n}`} onSelect={() => select(n)} className="gap-2 cursor-pointer">
                            <I className="h-4 w-4" strokeWidth={2} />
                            <span className="flex-1">{n}</span>
                            {n === niveau && <Check className="h-4 w-4" />}
                        </DropdownMenuItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
